import React, { Context, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  GiveawayResponse,
  isMissingAssets,
  isMissingCameraFlight,
  isRetryable,
  ItemNotFoundError,
  Locale,
  TaskStatus,
} from '@oneaudi/avp-hck-dga-customerui-commons';
import { changeLanguage } from 'i18next';
import { ExpiredSite } from '../../components/expiredSite/ExpiredSite';
import { Welcome } from '../../components/welcome/Welcome';
import { CameraFlight } from '../../components/camflight/Cameraflight';
import { Footer } from '../../components/footer/Footer';
import { Turntable } from '../../components/turntable/Turntable';
import { screen, size } from '../../common/breakpoints';
import { GiveawayContext } from '../../services/GiveawayContextService';
import { Spinner } from '../../components/spinner/Spinner';
import { ErrorSite } from '../../components/errorSite/ErrorSite';
import { Consumption } from '../../components/consumption/Consumption';
import { BeautyShot } from '../../components/beautyShot/BeautyShot';
import { ScrollToTop } from '../../components/scrolltotop/ScrollToTop';
import { Lowbar } from '../../components/lowbar/Lowbar';
import { KeyFacts } from '../../components/keyfacts/Keyfacts';
import { Exterior } from '../../components/exterior/exterior';
import { Interior } from '../../components/interior/interior';
import { MyDealer } from '../../components/myDealer/myDealer';
import { YourCar } from '../../components/yourcar/yourcar';
import { HorizontalSpacing, Spacing } from '../../common/spacings';
import { useInterval } from '../../hooks/useInterval';
import { CupraApiClient } from '../../client/CupraApiClient';
import { ApiClientContext } from '../../services/ApiClientService';
import { getI18nLocale, toLocaleOrEn } from '../../i18n/i18n';
import logo from '../../assets/logo.png';

export const StyledView = styled.div`
  width: 100%;

  @media (min-width: ${size.l}) {
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

export const StyledApp = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.1s;
  margin: 0 auto;
  @media (min-width: ${size.xxl}) {
    --max-width: ${size.xxl};
  }
  @media (min-width: ${size.xl}) and (max-width: ${size.xxl}) {
    --max-width: ${size.xxl};
  }
  @media (min-width: ${size.l}) and (max-width: ${size.xl}) {
    --max-width: ${size.xl};
  }
  @media (min-width: ${size.m}) and (max-width: ${size.l}) {
    --max-width: ${size.l};
  }
  @media (min-width: ${size.s}) and (max-width: ${size.m}) {
    --max-width: ${size.m};
  }
  @media (min-width: ${size.xs}) and (max-width: ${size.s}) {
    --max-width: ${size.s};
  }
  max-width: var(--max-width);
`;

const StyledMobileSection = styled.div`
  display: flex;
  justify-content: center;
  padding: ${HorizontalSpacing.M};

  @media screen and ${screen.l} {
    display: none;
  }
`;

const StyledLowBar = styled.div`
  display: none;

  @media screen and ${screen.l} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    z-index: 3;
    background-color: white;
    position: sticky;
    bottom: 0;
    width: inherit;
    padding: ${Spacing.L} 0;
    box-shadow:
      0px 2px 8px 0px #00000040,
      0px 1px 4px 0px #00000026;
  }
`;

const StyledMobileContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScrollToTopContainer = styled.div`
  display: none;

  @media screen and ${screen.l} {
    display: flex;
  }
`;

export const CupraHeader = styled.div`
  display: flex;
  justify-content: center;
`;

export const CupraLogo = styled.img`
  width: 20%;
  height: auto;
`;

const POLLING_INTERVAL = 10000;

const REMAINING_COMPONENTS = [
  <Interior key="interior" />,
  <Exterior key="exterior" />,
  <MyDealer key="myDealer" />,
];

const getComponentsSequence = (withCamFlight: boolean): ReactElement[] => {
  if (withCamFlight) {
    return [
      <CameraFlight key="cameraFlight" />,
      <Consumption key="consumption" />,
      <YourCar key="yourcar" />,
      <KeyFacts key="keyfacts" />,
      <Turntable key="turntable" />,
      ...REMAINING_COMPONENTS,
    ];
  }

  return [
    <Turntable key="turntable" />,
    <Consumption key="consumption" />,
    <YourCar key="yourcar" />,
    <KeyFacts key="keyfacts" />,
    ...REMAINING_COMPONENTS,
  ];
};

export const Giveaway = (): ReactElement => {
  const apiClient = useContext<CupraApiClient>(ApiClientContext as Context<CupraApiClient>);
  const giveawayId = useParams().giveawayId || undefined;
  const [giveawayData, setGiveawayData] = useState<GiveawayResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [responseStatus, setResponseStatus] = useState(200);
  const [locale, setLocale] = useState(Locale.en);

  const fetchGiveaway = useCallback(async () => {
    try {
      const response: GiveawayResponse = await apiClient.getOrder(giveawayId as string);
      setGiveawayData(response);
      setResponseStatus(200);

      setLocale(getI18nLocale(response.locale));
      await changeLanguage(locale);

      if (!isMissingAssets(response)) {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof ItemNotFoundError) {
        setResponseStatus(e.statusCode);
      }
      if (e instanceof TypeError) {
        // is thrown in case of errors like missing cors headers in response
        setResponseStatus(400);
      }
      console.error(e);
    }
  }, [apiClient, giveawayId, locale]);

  useEffect(() => {
    if (!giveawayId) {
      console.error('DGA id is missing.');
      setResponseStatus(404);
      return;
    }

    fetchGiveaway();
  }, [fetchGiveaway, giveawayId]);

  useInterval(
    fetchGiveaway,
    POLLING_INTERVAL,
    isRetryable(responseStatus) &&
      (isMissingAssets(giveawayData) || isMissingCameraFlight(giveawayData)),
  );

  if (responseStatus && responseStatus >= 400 && responseStatus < 500) {
    return (
      <ErrorSite errorKey={responseStatus === 500 ? 'temporalError' : 'error'} locale={locale} />
    );
  }

  if (!giveawayData) {
    return <></>;
  }

  if (isLoading) {
    return (
      <Spinner key="spinner" locale={giveawayData.locale ?? toLocaleOrEn(navigator.language)} />
    );
  }

  if (giveawayData.giveawayStatus === TaskStatus.EXPIRED) {
    return <ExpiredSite locale={locale} />;
  }

  const components: ReactElement[] = getComponentsSequence(
    giveawayData.renderCameraFlight ?? false,
  );

  return (
    <StyledView>
      <StyledApp data-testid="giveaway-container">
        <GiveawayContext.Provider value={giveawayData}>
          <CupraHeader>
            <CupraLogo src={logo} alt="Cupra" />
          </CupraHeader>
          <Welcome key="greeting" />
          {components[0]}
          {components[1]}
          <StyledMobileSection>
            <Lowbar key="sidebar" />
          </StyledMobileSection>
          <StyledMobileContent>{components.slice(2)}</StyledMobileContent>
          <BeautyShot key="beautyShot" />
          <StyledLowBar>
            <Lowbar key="sidebar" />
            <ScrollToTopContainer>
              <ScrollToTop />
            </ScrollToTopContainer>
          </StyledLowBar>
          <Footer locale={locale} market={giveawayData.market} onError={setResponseStatus} />
        </GiveawayContext.Provider>
      </StyledApp>
    </StyledView>
  );
};
